import { Controller } from "@hotwired/stimulus";

/**
 * Stimulus controller for cancellation modal with reason input
 *
 * This controller manages a modal dialog for cancelling records with a reason.
 * It works with Turbo forms and supports predefined reasons and custom input.
 *
 * Usage:
 *   <div data-controller="cancellation-modal"
 *        data-cancellation-modal-open-class="modal-open"
 *        data-cancellation-modal-close-class="modal-close">
 *     <button data-action="cancellation-modal#open">Cancel</button>
 *     <div data-cancellation-modal-target="backdrop" class="modal-close">
 *       <form data-cancellation-modal-target="form">
 *         <select data-cancellation-modal-target="reasonSelect"
 *                 data-action="change->cancellation-modal#onReasonChange">
 *           <option value="">Select a reason</option>
 *           <option value="error_entry">Error entry</option>
 *           <option value="other">Other</option>
 *         </select>
 *         <textarea data-cancellation-modal-target="customReason"></textarea>
 *         <input type="hidden" data-cancellation-modal-target="reasonInput" name="cancellation_reason">
 *       </form>
 *     </div>
 *   </div>
 */
export default class extends Controller {
  static targets = [
    "backdrop",
    "form",
    "reasonSelect",
    "customReason",
    "reasonInput",
    "submitButton",
  ];
  static classes = ["open", "close"];

  connect() {
    // Add escape key listener
    this.handleKeydown = this.handleKeydown.bind(this);
    document.addEventListener("keydown", this.handleKeydown);
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown);
  }

  open(event) {
    if (event) event.preventDefault();

    if (this.hasBackdropTarget) {
      this.backdropTarget.classList.remove(this.closeClass);
      this.backdropTarget.classList.add(this.openClass);
      document.body.classList.add("overflow-hidden");
    }

    // Reset form state
    this.resetForm();
  }

  close(event) {
    if (event) event.stopPropagation();

    if (this.hasBackdropTarget) {
      this.backdropTarget.classList.remove(this.openClass);
      this.backdropTarget.classList.add(this.closeClass);
      document.body.classList.remove("overflow-hidden");
    }
  }

  closeOnBackdrop(event) {
    // Only close if clicking directly on the backdrop, not its children
    if (event.target === this.backdropTarget) {
      this.close();
    }
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      this.close();
    }
  }

  onReasonChange(event) {
    const selectedValue = event.target.value;

    if (selectedValue === "other") {
      // Show custom reason textarea
      if (this.hasCustomReasonTarget) {
        this.customReasonTarget.classList.remove("hidden");
        this.customReasonTarget.required = true;
        this.customReasonTarget.focus();
      }
    } else {
      // Hide custom reason textarea
      if (this.hasCustomReasonTarget) {
        this.customReasonTarget.classList.add("hidden");
        this.customReasonTarget.required = false;
        this.customReasonTarget.value = "";
      }
    }

    this.updateReasonInput();
    this.validateForm();
  }

  onCustomReasonInput() {
    this.updateReasonInput();
    this.validateForm();
  }

  updateReasonInput() {
    if (!this.hasReasonInputTarget) return;

    const selectedValue = this.hasReasonSelectTarget
      ? this.reasonSelectTarget.value
      : "";

    if (selectedValue === "other" && this.hasCustomReasonTarget) {
      this.reasonInputTarget.value = this.customReasonTarget.value;
    } else {
      // Get the display text of the selected option for the reason
      const selectedOption = this.reasonSelectTarget?.selectedOptions[0];
      this.reasonInputTarget.value = selectedOption?.text || selectedValue;
    }
  }

  validateForm() {
    if (!this.hasSubmitButtonTarget) return;

    const selectedValue = this.hasReasonSelectTarget
      ? this.reasonSelectTarget.value
      : "";

    let isValid = false;

    if (selectedValue && selectedValue !== "other") {
      isValid = true;
    } else if (
      selectedValue === "other" &&
      this.hasCustomReasonTarget &&
      this.customReasonTarget.value.trim().length > 0
    ) {
      isValid = true;
    }

    this.submitButtonTarget.disabled = !isValid;

    if (isValid) {
      this.submitButtonTarget.classList.remove(
        "opacity-50",
        "cursor-not-allowed"
      );
    } else {
      this.submitButtonTarget.classList.add("opacity-50", "cursor-not-allowed");
    }
  }

  resetForm() {
    if (this.hasReasonSelectTarget) {
      this.reasonSelectTarget.value = "";
    }

    if (this.hasCustomReasonTarget) {
      this.customReasonTarget.value = "";
      this.customReasonTarget.classList.add("hidden");
      this.customReasonTarget.required = false;
    }

    if (this.hasReasonInputTarget) {
      this.reasonInputTarget.value = "";
    }

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true;
      this.submitButtonTarget.classList.add("opacity-50", "cursor-not-allowed");
    }
  }

  submitEnd(event) {
    if (event.detail.success) {
      this.close();
    }
  }
}
